<template>
  <div class="notification-wrapper">
    <!-- 顶栏按钮 + 红点徽标 -->
    <el-tooltip content="消息通知" placement="bottom">
      <el-badge
        :value="badgeValue"
        :max="99"
        :hidden="unreadCount === 0"
        class="notification-badge"
      >
        <el-button
          class="notification-btn"
          :class="{ 'has-unread': unreadCount > 0 }"
          @click="openDrawer"
        >
          <i class="el-icon-bell"></i>
        </el-button>
      </el-badge>
    </el-tooltip>

    <!-- 右侧抽屉 -->
    <el-drawer
      title="消息通知"
      :visible.sync="drawerVisible"
      direction="rtl"
      size="420px"
      :wrapper-closable="true"
      :destroy-on-close="true"
      @opened="onDrawerOpened"
    >
      <div class="drawer-header">
        <span class="drawer-tip">共 {{ total }} 条消息，{{ unreadCount }} 条未读</span>
        <el-button
          type="text"
          size="mini"
          :disabled="unreadCount === 0"
          @click="handleReadAll"
        >全部标为已读</el-button>
      </div>

      <div class="drawer-body" v-loading="loading" ref="scrollContainer">
        <el-empty
          v-if="!loading && messageList.length === 0"
          description="暂无消息"
          :image-size="80"
        ></el-empty>

        <div
          v-for="item in messageList"
          :key="item.id"
          class="message-item"
          :class="{ 'is-unread': item.isRead === 0 }"
          @click="handleItemClick(item)"
        >
          <div class="message-indicator" v-if="item.isRead === 0"></div>
          <div class="message-content">
            <div class="message-title-row">
              <span class="message-title">{{ item.title }}</span>
              <el-tag
                v-if="item.type"
                :type="typeTagMap[item.type] || 'info'"
                size="mini"
                effect="plain"
                class="message-type"
              >{{ typeLabelMap[item.type] || item.type }}</el-tag>
            </div>
            <div class="message-text">{{ item.content }}</div>
            <div class="message-time">{{ formatTime(item.createTime) }}</div>
          </div>
        </div>

        <div v-if="hasMore && !loading" class="load-more" @click="loadMore">
          <el-button type="text" size="mini" :loading="loadingMore">加载更多</el-button>
        </div>
        <div v-else-if="!loading && messageList.length > 0" class="load-more-end">— 没有更多了 —</div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { formatTime } from '@/utils/index'

export default {
  name: 'NotificationDrawer',
  data() {
    return {
      drawerVisible: false,
      messageList: [],
      loading: false,
      loadingMore: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      typeTagMap: {
        new_task: 'primary',
        task_timeout: 'warning',
        exception: 'danger',
        certificate_expiry: 'info',
        force_dispatch: 'danger'
      },
      typeLabelMap: {
        new_task: '新任务',
        task_timeout: '超时',
        exception: '异常',
        certificate_expiry: '证件',
        force_dispatch: '强制派单'
      }
    }
  },
  computed: {
    ...mapState('notification', ['unreadCount', 'pendingNotifications']),
    badgeValue() {
      return this.unreadCount > 99 ? '99+' : this.unreadCount
    },
    hasMore() {
      return this.messageList.length < this.total
    }
  },
  watch: {
    // 监听待弹窗队列, 每发现一条新未读就弹一次
    pendingNotifications: {
      handler(list) {
        if (!list || list.length === 0) return
        for (const msg of list) {
          this.$notify.error({
            title: msg.title || '消息通知',
            message: msg.content || '',
            position: 'top-right',
            duration: 0,           // 0 = 不自动关闭
            showClose: true
          })
        }
        // 弹完后清空, 避免重复弹
        this.clearPending()
      }
    }
  },
  methods: {
    ...mapActions('notification', [
      'fetchUnreadCount',
      'decrementUnread',
      'resetUnread',
      'clearPending'
    ]),

    openDrawer() {
      this.drawerVisible = true
    },

    async onDrawerOpened() {
      this.pageNum = 1
      this.messageList = []
      await this.loadList()
      // 抽屉打开时再刷一次未读数, 同步角标
      this.fetchUnreadCount()
      // 滚动到底部 (最新消息在底部)
      this.$nextTick(() => {
        const c = this.$refs.scrollContainer
        if (c) c.scrollTop = c.scrollHeight
      })
    },

    async loadList() {
      const isFirst = this.pageNum === 1
      isFirst ? (this.loading = true) : (this.loadingMore = true)
      try {
        const res = await this.api({
          url: '/driverMessage/list',
          method: 'post',
          data: {
            pageNum: this.pageNum,
            pageSize: this.pageSize
          }
        })
        if (res.status === 0) {
          const list = (res.data && res.data.list) || []
          this.total = (res.data && res.data.total) || 0
          this.messageList = isFirst ? list : [...this.messageList, ...list]
        } else {
          this.$message.error(res.message || '加载消息失败')
        }
      } catch (e) {
        this.$message.error('加载消息失败')
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      this.pageNum += 1
      this.loadList()
    },

    async handleItemClick(item) {
      // 已读消息不重复请求
      if (item.isRead === 1) return
      try {
        const res = await this.api({
          url: '/driverMessage/read',
          method: 'post',
          data: { id: item.id }
        })
        if (res.status === 0) {
          item.isRead = 1  // 本地更新避免重新拉取
          this.decrementUnread()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error('操作失败')
      }
    },

    async handleReadAll() {
      try {
        const res = await this.api({
          url: '/driverMessage/readAll',
          method: 'post'
        })
        if (res.status === 0) {
          // 本地全部标已读
          this.messageList.forEach(m => { m.isRead = 1 })
          this.resetUnread()
          this.$message.success('已全部标为已读')
        } else {
          this.$message.error(res.message || '操作失败')
        }
      } catch (e) {
        this.$message.error('操作失败')
      }
    },

    formatTime(t) {
      if (!t) return ''
      return formatTime(new Date(t).getTime() / 1000)
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-wrapper {
  display: inline-block;
  margin-right: 15px;
}

.notification-badge {
  ::v-deep .el-badge__content {
    border: 2px solid var(--bg-primary, #fff);
  }
}

.notification-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 8px;
  background-color: white;
  border: 1px solid var(--border-default, #E8EDF5);
  color: var(--text-secondary, #5a5e66);
  font-size: 18px;
  transition: all 0.2s;

  &:hover {
    background-color: var(--bg-hover, #f5f7fa);
    border-color: var(--primary, #165DFF);
    color: var(--primary, #165DFF);
  }

  &.has-unread {
    color: var(--primary, #165DFF);
  }
}

::v-deep .el-drawer__header {
  margin-bottom: 0;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-light, #F0F2F5);
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #1D2129);
}

::v-deep .el-drawer__body {
  padding: 0;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border-light, #F0F2F5);
  background: var(--bg-hover, #fafbfc);

  .drawer-tip {
    font-size: 12px;
    color: var(--text-secondary, #5a5e66);
  }
}

.drawer-body {
  padding: 0;
  height: calc(100vh - 110px);
  overflow-y: auto;
}

.message-item {
  display: flex;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-light, #F0F2F5);
  cursor: pointer;
  transition: background 0.15s;
  position: relative;

  &:hover {
    background: var(--bg-hover, #fafbfc);
  }

  &.is-unread .message-title {
    font-weight: 600;
    color: var(--text-primary, #1D2129);
  }

  &.is-unread .message-text {
    color: var(--text-primary, #1D2129);
  }

  &:not(.is-unread) .message-title,
  &:not(.is-unread) .message-text {
    color: var(--text-secondary, #5a5e66);
  }
}

.message-indicator {
  width: 4px;
  background: var(--primary, #165DFF);
  border-radius: 2px;
  margin-right: 12px;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
  min-width: 0;
}

.message-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.message-title {
  font-size: 14px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.message-type {
  flex-shrink: 0;
}

.message-text {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.message-time {
  font-size: 12px;
  color: var(--text-muted, #86909C);
}

.load-more, .load-more-end {
  text-align: center;
  padding: 16px 0;
  font-size: 12px;
  color: var(--text-muted, #86909C);
}

// 深色主题适配
.dark-theme {
  .notification-btn {
    background-color: var(--bg-card, #2D3548);
    border-color: var(--border-default, #3A4258);
    color: var(--text-primary, #F5F7FA);
  }
  .message-item {
    border-color: var(--border-default, #3A4258);
    &.is-unread .message-title,
    &.is-unread .message-text {
      color: var(--text-primary, #F5F7FA);
    }
    &:not(.is-unread) .message-title,
    &:not(.is-unread) .message-text {
      color: var(--text-secondary, #B8C1D1);
    }
  }
  .drawer-header {
    background: var(--bg-card, #2D3548);
    border-color: var(--border-default, #3A4258);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/NotificationDrawer/index.vue